<app-side-menu>
  <div class="poq-wrap">
    <div *ngIf="isLoading" class="loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Carregando...</span>
      </div>
    </div>

    <div class="poq-header" *ngIf="!isLoading && purchaseOrderId">
      <div class="d-flex justify-content-between align-items-start flex-wrap gap-3">
        <div>
          <h1 class="poq-title">
            <i class="fas fa-balance-scale me-2"></i>
            Cotações — {{ purchaseOrder?.orderNumber || ('Pedido #' + purchaseOrderId) }}
          </h1>
          <p class="poq-subtitle mb-0">
            Até {{ maxProposals }} orçamentos por pedido. Escolha o vencedor ao final do processo.
          </p>
        </div>
        <div class="d-flex gap-2 flex-wrap">
          <button type="button" class="btn btn-light border" (click)="goBack()">
            <i class="fas fa-arrow-left me-1"></i>
            Voltar ao pedido
          </button>
        </div>
      </div>
    </div>

    <div class="alert alert-light border" *ngIf="!isLoading && purchaseOrderId && !header">
      <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <strong>Nenhum processo de cotação</strong>
          <p class="mb-0 small text-muted">Inicie um processo para registrar até {{ maxProposals }} propostas de fornecedores.</p>
        </div>
        <button
          type="button"
          class="btn btn-primary"
          *appHasPermission="'PurchaseOrders.Edit'"
          [disabled]="isSavingHeader || !staffId"
          (click)="startQuotationProcess()">
          <i class="fas fa-play me-1" *ngIf="!isSavingHeader"></i>
          <i class="fas fa-spinner fa-spin me-1" *ngIf="isSavingHeader"></i>
          Iniciar cotação
        </button>
      </div>
    </div>

    <div *ngIf="!isLoading && header" class="poq-process">
      <div class="poq-card mb-3">
        <div class="poq-card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div class="d-flex align-items-center gap-2">
            <i class="fas fa-folder-open text-muted"></i>
            <h5 class="mb-0">Processo {{ header.headerNumber || ('#' + header.id) }}</h5>
          </div>
        </div>
        <div class="poq-card-body">
          <div class="row g-3 align-items-center">
            <div class="col-md-3">
              <div class="meta-label">Status</div>
              <span class="status-pill status-pill-open">{{ translateStatus(header.status) }}</span>
              <span class="status-pill status-pill-winner ms-1" *ngIf="hasWinner()">Vencedor definido</span>
            </div>
            <div class="col-md-4">
              <div class="meta-label">Válido até</div>
              <div>{{ formatWhen(header.validUntil) }}</div>
            </div>
            <div class="col-md-5 text-md-end">
              <ng-container *appHasPermission="'PurchaseOrders.Edit'">
                <button type="button" class="btn btn-process-cancel btn-sm me-2" *ngIf="canCancelHeader()" (click)="openCancelHeaderModal()">
                  <i class="fas fa-circle-xmark me-1"></i>
                  Cancelar Processo
                </button>
                <button type="button" class="btn btn-process-delete btn-sm" *ngIf="canDeleteHeader()" (click)="deleteHeader()">
                  <i class="fas fa-trash me-1"></i>
                  Excluir
                </button>
              </ng-container>
            </div>
          </div>
          <p class="small text-muted mt-2 mb-0" *ngIf="header.notes">{{ header.notes }}</p>
        </div>
      </div>

      <div class="poq-card mb-3" *ngIf="purchaseOrder?.items?.length">
        <div class="poq-card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div class="d-flex align-items-center gap-2">
            <i class="fas fa-boxes-stacked text-muted"></i>
            <h5 class="mb-0">Itens do pedido <span class="small text-muted">(visão situacional)</span></h5>
          </div>
        </div>
        <div class="poq-card-body p-0">
          <div class="table-responsive">
            <table class="table table-hover align-middle mb-0">
              <thead class="table-head-custom">
                <tr>
                  <th>Item</th>
                  <th class="text-center">Qtd</th>
                  <th>Unidade</th>
                  <th>Status</th>
                  <th class="text-end">{{ itemsTotalColumnLabel }}</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let it of purchaseOrder?.items">
                  <td>{{ it.productName || it.itemName || '—' }}</td>
                  <td class="text-center">{{ it.quantityRequested }}</td>
                  <td>{{ it.unit || '—' }}</td>
                  <td><span class="status-pill status-pill-neutral">{{ translateStatus(it.status) }}</span></td>
                  <td class="text-end fw-semibold">{{ formatMoney(displayItemTotal(it)) }}</td>
                </tr>
              </tbody>
              <tfoot class="table-foot-custom">
                <tr>
                  <td colspan="4" class="text-end fw-semibold">{{ itemsTotalFooterLabel }}</td>
                  <td class="text-end fw-bold fs-6 text-primary">{{ formatMoney(displayItemsTotal()) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      <div class="poq-card">
        <div class="poq-card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div class="d-flex align-items-center gap-2">
            <i class="fas fa-file-text text-muted"></i>
            <h5 class="mb-0">Propostas <span class="badge text-bg-secondary ms-1">{{ proposals.length }} / {{ maxProposals }}</span></h5>
          </div>
          <button type="button" class="btn btn-success btn-sm" *appHasPermission="'PurchaseOrders.Edit'"
            [disabled]="!canMutateProposals() || proposals.length >= maxProposals || !staffId" (click)="openCreateProposal()">
            <i class="fas fa-plus me-1"></i>
            Nova proposta
            <span class="text-white-50 ms-1" *ngIf="proposalSlotsRemaining() === 0">(limite)</span>
          </button>
        </div>
        <div class="poq-card-body p-0">
          <div class="alert alert-warning py-2 m-3" *ngIf="headerLocked() && proposals.length">
            <small>Este processo está encerrado ou já possui vencedor — edição bloqueada.</small>
          </div>
          <div class="table-responsive poq-table-wrap" *ngIf="proposals.length; else emptyProps">
        <table class="table table-hover align-middle poq-table mb-0">
          <thead class="table-head-custom">
            <tr>
              <th>Fornecedor</th>
              <th>Nº proposta</th>
              <th class="text-end">Total</th>
              <th class="text-center">Prazo (dias)</th>
              <th>Status</th>
              <th class="text-end">Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let q of proposals" [class.table-success]="q.isWinner">
              <td>
                {{ supplierLabel(q) }}
                <i class="fas fa-trophy text-warning ms-1" *ngIf="q.isWinner" title="Vencedor"></i>
              </td>
              <td>{{ q.quotationNumber || '—' }}</td>
              <td class="text-end"><strong>{{ formatMoney(q.totalAmount) }}</strong></td>
              <td class="text-center">{{ q.deliveryLeadTimeDays ?? '—' }}</td>
              <td><span class="status-pill status-pill-received">{{ translateStatus(q.status) }}</span></td>
              <td class="text-end text-nowrap">
                <ng-container *appHasPermission="'PurchaseOrders.Edit'">
                  <button type="button"
                    class="btn btn-icon-action text-info position-relative"
                    title="Anexos da proposta"
                    (click)="openDocumentsModal(q)">
                    <i class="fas fa-paperclip"></i>
                    <span class="badge bg-info position-absolute top-0 start-100 translate-middle"
                      style="font-size: 0.6rem;"
                      *ngIf="documentCount(q.id) > 0">
                      {{ documentCount(q.id) }}
                    </span>
                  </button>
                  <button type="button" class="btn btn-icon-action" title="Editar" (click)="openEditProposal(q)" *ngIf="canMutateProposals()">
                    <i class="fas fa-pen"></i>
                  </button>
                  <button type="button" class="btn btn-icon-action text-warning" title="Cancelar"
                    (click)="openCancelProposal(q)" *ngIf="canMutateProposals() && !q.isWinner">
                    <i class="fas fa-circle-xmark"></i>
                  </button>
                  <button type="button" class="btn btn-icon-action text-danger" title="Excluir"
                    (click)="deleteProposal(q)" *ngIf="canMutateProposals() && !q.isWinner">
                    <i class="fas fa-trash"></i>
                  </button>
                  <span class="vr mx-2" *ngIf="canSelectWinner() && !q.isWinner"></span>
                  <button type="button" class="btn btn-sm btn-outline-success"
                    (click)="openWinnerModal(q)" *ngIf="canSelectWinner() && !q.isWinner">
                    <i class="fas fa-trophy me-1"></i>
                    Vencedor
                  </button>
                </ng-container>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <ng-template #emptyProps>
        <div class="alert alert-secondary m-3">
          Nenhuma proposta registrada ainda. Limite de {{ maxProposals }} orçamentos por processo.
        </div>
      </ng-template>
      </div>
      </div>
    </div>

    <!-- Modal proposta -->
    <app-modal
      [isOpen]="showProposalModal"
      [title]="proposalModalMode === 'create' ? 'Nova proposta' : 'Editar proposta'"
      [size]="'lg'"
      [closable]="true"
      (close)="closeProposalModal()">
      <div class="row g-2" *ngIf="showProposalModal">
        <div class="col-md-6">
          <label class="form-label">Fornecedor <span class="text-danger">*</span></label>
          <select class="form-select" [(ngModel)]="proposalForm.supplierId">
            <option [ngValue]="null">Selecione...</option>
            <option *ngFor="let s of suppliers" [ngValue]="s.id">{{ s.companyName }}{{ s.tradeName ? ' — ' + s.tradeName : '' }}</option>
          </select>
        </div>
        <div class="col-md-6">
          <label class="form-label">Código da proposta</label>
          <input type="text" class="form-control" [(ngModel)]="proposalForm.quotationNumber" readonly maxlength="80" />
          <small class="text-muted">Gerado automaticamente na criação; não é editável.</small>
        </div>
        <div class="col-md-6">
          <label class="form-label">Data da proposta</label>
          <input type="datetime-local" class="form-control" [(ngModel)]="proposalForm.quotationDate" />
        </div>
        <div class="col-md-6">
          <label class="form-label">Válida até</label>
          <input type="datetime-local" class="form-control" [(ngModel)]="proposalForm.validUntil" />
        </div>
        <div class="col-md-4">
          <label class="form-label">Moeda</label>
          <select class="form-select" [(ngModel)]="proposalForm.currencyCode">
            <option *ngFor="let c of currencyOptions" [ngValue]="c.code">
              {{ c.symbol }} — {{ c.label }}
            </option>
          </select>
        </div>
        <div class="col-md-4">
          <label class="form-label">Valor total da compra</label>
          <input
            type="text"
            inputmode="numeric"
            class="form-control"
            appCurrencyShiftMask
            [(ngModel)]="proposalForm.itemsSubtotal"
            (ngModelChange)="recalculateProposalTotal()" />
        </div>
        <div class="col-md-4">
          <label class="form-label">Desconto</label>
          <input
            type="text"
            inputmode="numeric"
            class="form-control"
            appCurrencyShiftMask
            [(ngModel)]="proposalForm.discountAmount"
            (ngModelChange)="recalculateProposalTotal()" />
        </div>
        <div class="col-md-4">
          <label class="form-label">Frete</label>
          <input
            type="text"
            inputmode="numeric"
            class="form-control"
            appCurrencyShiftMask
            [(ngModel)]="proposalForm.freightAmount"
            (ngModelChange)="recalculateProposalTotal()" />
        </div>
        <div class="col-md-4">
          <label class="form-label">Outros custos</label>
          <input
            type="text"
            inputmode="numeric"
            class="form-control"
            appCurrencyShiftMask
            [(ngModel)]="proposalForm.otherCostsAmount"
            (ngModelChange)="recalculateProposalTotal()" />
        </div>
        <div class="col-md-4">
          <label class="form-label">Impostos</label>
          <input
            type="text"
            inputmode="numeric"
            class="form-control"
            appCurrencyShiftMask
            [(ngModel)]="proposalForm.taxAmount"
            (ngModelChange)="recalculateProposalTotal()" />
        </div>
        <div class="col-md-4">
          <label class="form-label">Total</label>
          <input
            type="text"
            inputmode="numeric"
            class="form-control"
            appCurrencyShiftMask
            [(ngModel)]="proposalForm.totalAmount" />
          <button type="button" class="btn btn-link btn-sm px-0" (click)="recalculateProposalTotal()">Recalcular</button>
        </div>
        <div class="col-md-4">
          <label class="form-label">Prazo pagamento (dias)</label>
          <input type="number" class="form-control" [(ngModel)]="proposalForm.paymentTermDays" />
        </div>
        <div class="col-md-4">
          <label class="form-label">Prazo entrega (dias)</label>
          <input type="number" class="form-control" [(ngModel)]="proposalForm.deliveryLeadTimeDays" />
        </div>
        <div class="col-md-4">
          <label class="form-label">Status API</label>
          <input type="text" class="form-control" [(ngModel)]="proposalForm.status" maxlength="40" />
        </div>
        <div class="col-12">
          <label class="form-label">Observações</label>
          <textarea class="form-control" rows="2" [(ngModel)]="proposalForm.notes"></textarea>
        </div>
      </div>
      <div slot="footer" class="d-flex justify-content-end gap-2">
        <button type="button" class="btn btn-secondary" (click)="closeProposalModal()" [disabled]="isSavingProposal">Fechar</button>
        <button type="button" class="btn btn-primary" (click)="submitProposal()" [disabled]="isSavingProposal">
          <i class="fas fa-spinner fa-spin me-1" *ngIf="isSavingProposal"></i>
          Salvar
        </button>
      </div>
    </app-modal>

    <!-- Modal vencedor -->
    <app-modal
      [isOpen]="showWinnerModal"
      [title]="'Definir fornecedor vencedor'"
      [size]="'lg'"
      [closable]="true"
      (close)="closeWinnerModal()">
      <p class="small text-muted mb-2">
        Preço unitário e quantidade vêm do orçamento da proposta vencedora e do item do pedido (somente leitura).
      </p>
      <label class="form-label">Motivo da escolha <span class="text-danger">*</span></label>
      <textarea class="form-control mb-3" rows="3" [(ngModel)]="winnerReason" maxlength="500" placeholder="Ex.: melhor custo-benefício e prazo de entrega"></textarea>
      <label class="form-label">Itens do pedido <span class="text-danger">*</span></label>
      <div class="text-center py-4 border rounded mb-0 bg-light" *ngIf="isLoadingWinnerLines">
        <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
        <p class="small text-muted mb-0 mt-2">Carregando valores do orçamento…</p>
      </div>
      <div class="table-responsive border rounded mb-0" *ngIf="!isLoadingWinnerLines && winnerLines.length; else winnerNoLines">
        <table class="table table-sm align-middle mb-0">
          <thead class="table-light">
            <tr>
              <th>Item</th>
              <th class="text-center">Un.</th>
              <th class="text-center">Quantidade (item do pedido)</th>
              <th class="text-end">Preço unit. (orçamento vencedor)</th>
              <th class="text-end">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let row of winnerLines">
              <td>{{ row.label }}</td>
              <td class="text-center">{{ row.unit || '—' }}</td>
              <td class="text-center">{{ row.quantityApproved ?? '—' }}</td>
              <td class="text-end">
                <span *ngIf="row.actualUnitPrice != null">{{ row.actualUnitPrice | currency: 'BRL': 'symbol': '1.2-2':'pt-BR' }}</span>
                <span *ngIf="row.actualUnitPrice == null" class="text-muted">—</span>
              </td>
              <td class="text-end">
                <span *ngIf="winnerLineTotal(row) != null">{{ winnerLineTotal(row) | currency: 'BRL': 'symbol': '1.2-2':'pt-BR' }}</span>
                <span *ngIf="winnerLineTotal(row) == null" class="text-muted">—</span>
              </td>
            </tr>
          </tbody>
          <tfoot class="table-light">
            <tr>
              <th colspan="4" class="text-end">Preço total da compra</th>
              <th class="text-end">{{ winnerOverallTotal | currency: 'BRL': 'symbol': '1.2-2':'pt-BR' }}</th>
            </tr>
          </tfoot>
        </table>
      </div>
      <ng-template #winnerNoLines>
        <div class="alert alert-warning py-2 small mb-0" *ngIf="!isLoadingWinnerLines">
          {{
            winnerQuotationId
              ? 'Não foi possível montar as linhas com valores do orçamento. Confira a proposta no servidor.'
              : 'Sem linhas de item neste pedido. Não é possível concluir a escolha do vencedor.'
          }}
        </div>
      </ng-template>
      <div slot="footer" class="d-flex justify-content-end gap-2">
        <button type="button" class="btn btn-secondary" (click)="closeWinnerModal()" [disabled]="isSelectingWinner">Cancelar</button>
        <button
          type="button"
          class="btn btn-success"
          (click)="confirmWinner()"
          [disabled]="isSelectingWinner || isLoadingWinnerLines || !winnerLinesHaveValidPrices()">
          <i class="fas fa-spinner fa-spin me-1" *ngIf="isSelectingWinner"></i>
          Confirmar
        </button>
      </div>
    </app-modal>

    <!-- Cancelar processo -->
    <app-modal
      [isOpen]="showCancelHeaderModal"
      [title]="'Cancelar processo de cotação'"
      [size]="'md'"
      [closable]="true"
      (close)="closeCancelHeaderModal()">
      <label class="form-label">Motivo <span class="text-danger">*</span></label>
      <textarea class="form-control" rows="3" [(ngModel)]="cancelHeaderReason" maxlength="500"></textarea>
      <div slot="footer" class="d-flex justify-content-end gap-2">
        <button type="button" class="btn btn-secondary" (click)="closeCancelHeaderModal()">Voltar</button>
        <button type="button" class="btn btn-danger" (click)="confirmCancelHeader()">Cancelar processo</button>
      </div>
    </app-modal>

    <!-- Cancelar proposta -->
    <app-modal
      [isOpen]="showCancelProposalModal"
      [title]="'Cancelar proposta'"
      [size]="'md'"
      [closable]="true"
      (close)="closeCancelProposalModal()">
      <label class="form-label">Motivo <span class="text-danger">*</span></label>
      <textarea class="form-control" rows="3" [(ngModel)]="cancelProposalReason" maxlength="500"></textarea>
      <div slot="footer" class="d-flex justify-content-end gap-2">
        <button type="button" class="btn btn-secondary" (click)="closeCancelProposalModal()">Voltar</button>
        <button type="button" class="btn btn-warning" (click)="confirmCancelProposal()">Confirmar</button>
      </div>
    </app-modal>

    <!-- Portal de anexos da proposta -->
    <app-modal
      [isOpen]="showDocumentsModal"
      [title]="'Anexos da proposta' + (documentsModalQuotation ? ' — ' + supplierLabel(documentsModalQuotation) : '')"
      [size]="'lg'"
      [closable]="true"
      (close)="closeDocumentsModal()">
      <div *ngIf="showDocumentsModal">
        <!-- Formulário de upload -->
        <div class="border rounded p-3 mb-3 bg-light-subtle">
          <h6 class="mb-3"><i class="fas fa-cloud-upload-alt me-2 text-primary"></i>Enviar novo anexo</h6>
          <div class="row g-2">
            <div class="col-md-6">
              <label class="form-label">Arquivo <span class="text-danger">*</span></label>
              <input type="file" class="form-control"
                (change)="onDocumentFilePicked($event)"
                [disabled]="isUploadingDocument"
                accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx,.xls,.xlsx,.csv,.zip,.rar,.txt" />
              <small class="text-muted">Tamanho máximo: 15 MB.</small>
            </div>
            <div class="col-md-3">
              <label class="form-label">Categoria <span class="text-danger">*</span></label>
              <select class="form-select"
                [(ngModel)]="documentForm.category"
                [disabled]="isUploadingDocument">
                <option *ngFor="let c of documentCategoryOptions" [ngValue]="c.value">
                  {{ c.label }}
                </option>
              </select>
            </div>
            <div class="col-md-3 d-flex align-items-end">
              <button type="button" class="btn btn-primary w-100"
                (click)="submitDocumentUpload()"
                [disabled]="isUploadingDocument || !documentForm.file">
                <i class="fas fa-spinner fa-spin me-1" *ngIf="isUploadingDocument"></i>
                <i class="fas fa-upload me-1" *ngIf="!isUploadingDocument"></i>
                Enviar
              </button>
            </div>
            <div class="col-12">
              <label class="form-label">Descrição (opcional)</label>
              <input type="text" class="form-control"
                [(ngModel)]="documentForm.description"
                [disabled]="isUploadingDocument"
                maxlength="200"
                placeholder="Ex.: Cotação Nexos / fornecedor" />
            </div>
          </div>
        </div>

        <!-- Lista de anexos -->
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h6 class="mb-0"><i class="fas fa-folder-open me-2 text-warning"></i>Documentos anexados</h6>
          <button type="button" class="btn btn-sm btn-outline-secondary"
            (click)="loadDocumentsList()"
            [disabled]="isLoadingDocuments">
            <i class="fas fa-sync-alt" [class.fa-spin]="isLoadingDocuments"></i>
          </button>
        </div>

        <div class="text-center py-4 border rounded bg-light" *ngIf="isLoadingDocuments">
          <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
          <small class="d-block mt-2 text-muted">Carregando anexos...</small>
        </div>

        <div class="alert alert-secondary mb-0" *ngIf="!isLoadingDocuments && documents.length === 0">
          Nenhum anexo registrado para esta proposta ainda.
        </div>

        <div class="table-responsive" *ngIf="!isLoadingDocuments && documents.length > 0">
          <table class="table table-sm table-hover align-middle mb-0">
            <thead class="table-light">
              <tr>
                <th>Arquivo</th>
                <th>Categoria</th>
                <th>Descrição</th>
                <th>Enviado em</th>
                <th class="text-end">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let d of documents">
                <td>
                  <i [class]="documentIconClass(d)" class="me-2"></i>
                  <span class="fw-semibold">{{ d.fileName || '(sem nome)' }}</span>
                </td>
                <td>
                  <span class="badge bg-info-subtle text-dark border">{{ documentCategoryLabel(d.category) }}</span>
                </td>
                <td class="text-muted small">{{ d.description || '—' }}</td>
                <td class="small">{{ formatWhen(d.uploadedAt) }}</td>
                <td class="text-end text-nowrap">
                  <button type="button" class="btn btn-icon-action text-primary" title="Abrir/baixar"
                    (click)="openDocumentLink(d)"
                    [disabled]="!d.filePath">
                    <i class="fas fa-download"></i>
                  </button>
                  <button type="button" class="btn btn-icon-action text-danger" title="Excluir anexo"
                    *appHasPermission="'PurchaseOrders.Edit'"
                    (click)="deleteQuotationDocument(d)"
                    [disabled]="deletingDocumentId === d.id">
                    <i class="fas fa-trash" *ngIf="deletingDocumentId !== d.id"></i>
                    <i class="fas fa-spinner fa-spin" *ngIf="deletingDocumentId === d.id"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div slot="footer" class="d-flex justify-content-end">
        <button type="button" class="btn btn-secondary" (click)="closeDocumentsModal()" [disabled]="isUploadingDocument">Fechar</button>
      </div>
    </app-modal>
  </div>
</app-side-menu>
